/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Directory page logic module.
 * Handles company search and directory-specific API operations.
 * @module logic/directory
 */

import { refreshToken, signOut } from "../../utils/common.js";

const BROWSE_PAGE_DELAY_MS = 250;
const DEFAULT_SEARCH_PAGE_SIZE = 50;

const EMPTY_PAGE_RESULT = { items: [], total: 0, lastSort: null };

/**
 * Fetches companies from OpenSearch with browse pagination
 * @param {Function} navigate - Navigation function
 * @param {Array} lastSort - Array representing the sort values of the last hit from previous page
 * @returns {Promise<{items: Object[], total: number, lastSort: Array|null}>}
 */
export const browseCompaniesFromOpensearch = async (
  navigate,
  lastSort = null,
) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({
      searchAfter: lastSort,
    }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}company/browse`,
      options,
    );
    if (!response.ok) {
      throw new Error(`OpenSearch request failed: ${response.statusText}`);
    }

    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return EMPTY_PAGE_RESULT;
      }
      return await browseCompaniesFromOpensearch(navigate, lastSort);
    }

    return {
      items: data.items || [],
      total: data.total || 0,
      lastSort: data?.lastSort || null,
    };
  } catch (error) {
    logger.error("Error searching companies:", { err: sanitizeError(error) });
    return EMPTY_PAGE_RESULT;
  }
};

/**
 * Sleeps for a given number of milliseconds
 * @param {number} ms - Number of milliseconds to sleep
 * @returns {Promise<void>}
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Preloads all companies from OpenSearch with browse pagination
 * @param {Function} navigate - Navigation function
 * @param {Array} initialCursor - Cursor for the page after `initialItems`
 * @param {Array} initialItems - Companies already loaded (first page)
 * @param {Function} setCompaniesData - Sets the full accumulated company list
 * @param {Function} setLastSort - Updates the OpenSearch pagination cursor
 * @param {Function} setLimitReached - Marks when all pages have been loaded
 * @returns {Promise<void>}
 */
export const preloadAllCompanies = async (
  navigate,
  initialCursor,
  initialItems,
  setCompaniesData,
  setLastSort,
  setLimitReached,
) => {
  let cursor = initialCursor;
  let accumulated = [...(initialItems || [])];

  while (cursor) {
    const response = await browseCompaniesFromOpensearch(navigate, cursor);
    if (!Array.isArray(response.items)) {
      break;
    }

    accumulated = [...accumulated, ...response.items];
    setCompaniesData(accumulated);
    cursor = response.lastSort;
    setLastSort?.(cursor ?? null);
    if (!cursor) {
      setLimitReached(true);
    }
    await sleep(BROWSE_PAGE_DELAY_MS);
  }
};

/**
 * Fetches companies from OpenSearch with search_after pagination
 * @param {string} searchQuery - The search text
 * @param {number} size - Number of results per page
 * @param {Array} lastSort - Array representing the sort values of the last hit from previous page
 * @returns {Promise<{items: Object[], total: number, lastSort: Array|null}>}
 */
export const fetchCompaniesFromOpenSearch = async (
  navigate,
  searchParameters,
  size = DEFAULT_SEARCH_PAGE_SIZE,
  lastSort = null,
) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({
      searchParameters,
      size,
      searchAfter: lastSort,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}company/search`,
      options,
    );
    if (!response.ok) {
      throw new Error(`OpenSearch request failed: ${response.statusText}`);
    }

    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return EMPTY_PAGE_RESULT;
      }
      return await fetchCompaniesFromOpenSearch(
        navigate,
        searchParameters,
        size,
        lastSort,
      );
    }

    return {
      items: data.items || [],
      total: data.total || 0,
      lastSort: data?.lastSort || null,
    };
  } catch (error) {
    logger.error("Error searching companies:", { err: sanitizeError(error) });
    return EMPTY_PAGE_RESULT;
  }
};

/**
 * Preloads the remaining companies from OpenSearch with search_after pagination
 * @param {Function} navigate - Navigation function
 * @param {Object} searchParameters - The search parameters
 * @param {Array} initialCursor - Cursor for the page after `initialItems`
 * @param {Array} initialItems - Companies already loaded (first page)
 * @param {Function} setCompaniesData - Sets the full accumulated company list
 * @param {Function} setLastSort - Updates the OpenSearch pagination cursor
 * @param {Function} setLimitReached - Marks when all pages have been loaded
 * @returns {Promise<void>}
 */
export const preloadTheRemainingCompanies = async (
  navigate,
  searchParameters,
  initialCursor,
  initialItems,
  setCompaniesData,
  setLastSort,
  setLimitReached,
) => {
  let cursor = initialCursor;
  let accumulated = [...(initialItems || [])];

  while (cursor) {
    const response = await fetchCompaniesFromOpenSearch(
      navigate,
      searchParameters,
      null,
      cursor,
    );
    if (!Array.isArray(response.items)) {
      break;
    }

    accumulated = [...accumulated, ...response.items];
    setCompaniesData(accumulated);
    cursor = response.lastSort;
    setLastSort?.(cursor ?? null);
    if (!cursor) {
      setLimitReached?.(true);
    }
    await sleep(BROWSE_PAGE_DELAY_MS);
  }
};
